import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ApiCallService} from '../helpers/api-call.service';
import {MediaService} from '../helpers/media.service';

/**
 * OAuth consent page for the MediaBlast MCP server.
 *
 * The backend's /mcp/oauth/authorize redirects the browser here with an opaque
 * ?request_id=. This route is guarded by AuthenticationGuard, so an
 * unauthenticated user is first sent through the normal AMC website login
 * (including 2FA) and returned here — no separate/reduced MCP login UI.
 *
 * On Allow we POST the request_id to /client/oauth/approve (x-access-token is
 * attached automatically by HeadersInterceptor); the backend binds the
 * logged-in user to a grant, mints the PKCE-bound code, and returns the
 * client's redirect URL, which we navigate to (back to the AI client).
 */
@Component({
  selector: 'app-oauth-consent',
  templateUrl: './oauth-consent.component.html',
  styleUrls: ['./oauth-consent.component.scss']
})
export class OAuthConsentComponent implements OnInit {
  requestId = '';
  email = '';
  // Neutral label shown only briefly while the pending-info call is in flight.
  // If it's still showing after the page settles, that itself is diagnostic:
  // it's DISTINCT from the backend's own fallbacks ('Unknown MCP client' =
  // pending found a client-less clientId, a server-side data gap; 'Unnamed MCP
  // client' = client row exists but never sent a name at DCR time) — this one
  // means the lookup call never completed (see clientNameError / Network tab).
  clientName = 'An AI assistant';
  clientNameError = ''; // set only if the pending-info call itself fails
  submitting = false; // true while EITHER allow() or deny() is in flight (disables both buttons)
  denying = false; // true only while deny() specifically is in flight (button label)
  errorMsg = '';

  constructor(private route: ActivatedRoute,
              private router: Router,
              private apiCallService: ApiCallService,
              private mediaService: MediaService) {
  }

  ngOnInit(): void {
    this.requestId = this.route.snapshot.queryParams['request_id'] || '';
    if (!this.requestId) {
      this.errorMsg = 'Missing request_id. Please start the connection from your AI client again.';
    } else {
      // Show which client is actually asking for access (e.g. "Drashti MB MCP")
      // instead of a generic label, so the user can verify what they're
      // authorizing. Cosmetic only — approval itself is unaffected either way.
      this.apiCallService.oauthPendingInfo(this.requestId).subscribe({
        next: (resp: any) => {
          if (resp && resp.clientName) {
            this.clientName = resp.clientName;
          }
        },
        error: (err: any) => {
          // Distinct, logged failure mode: the lookup call itself errored
          // (network/auth/CORS, or the request_id was already invalid at this
          // point) — never silently keep the generic label without a trace.
          this.clientNameError = (err && err.status) ? ('HTTP ' + err.status) : 'network error';
          // eslint-disable-next-line no-console
          console.error('[oauth-consent] pending-info lookup failed:', this.clientNameError, err);
        }
      });
    }
    // Show who they are; if currentUser isn't loaded yet, fetch it (cosmetic only —
    // the backend derives identity from the token, never from this page).
    this.email = (this.mediaService.currentUser && this.mediaService.currentUser.email) || '';
    if (!this.email) {
      this.apiCallService.authentication().subscribe({
        next: (resp: any) => {
          this.email = (resp && resp.user && resp.user.email) ||
            (this.mediaService.currentUser && this.mediaService.currentUser.email) || '';
        },
        error: () => { /* email stays blank; not required for approval */ }
      });
    }
  }

  allow(): void {
    if (!this.requestId || this.submitting) {
      return;
    }
    this.submitting = true;
    this.errorMsg = '';
    this.apiCallService.oauthApprove(this.requestId).subscribe({
      next: (resp: any) => {
        if (resp && resp.redirectUrl) {
          window.location.href = resp.redirectUrl;
        } else {
          this.submitting = false;
          this.errorMsg = (resp && resp.error && resp.error.message) || 'Approval failed. Please try again.';
        }
      },
      error: (err: any) => {
        this.submitting = false;
        this.errorMsg = (err && err.error && err.error.error && err.error.error.message) ||
          'Approval failed. Please try again.';
      }
    });
  }

  deny(): void {
    if (!this.requestId || this.submitting) {
      return;
    }
    this.submitting = true;
    this.denying = true;
    this.errorMsg = '';
    this.apiCallService.oauthDeny(this.requestId).subscribe({
      next: (resp: any) => {
        if (resp && resp.redirectUrl) {
          // Send the popup back to the CONNECTING CLIENT'S OWN redirect_uri
          // with error=access_denied (RFC 6749 §4.1.2.1) — the same completion
          // signal approve() sends on success (?code=...), just an error. The
          // connecting client (e.g. Proposaly) is watching for its own
          // redirect_uri to be reached to know the flow ended; without this,
          // its "waiting to sign in" UI has no way to know the user said no.
          window.location.href = resp.redirectUrl;
        } else {
          this.submitting = false;
          this.denying = false;
          this.fallbackClose();
        }
      },
      error: () => {
        this.submitting = false;
        this.denying = false;
        this.fallbackClose();
      }
    });
  }

  // Used only when we can't reach the client's own redirect_uri (no
  // requestId, or the deny call itself failed) — never the primary path.
  private fallbackClose(): void {
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    this.router.navigate(['/media-blast']);
  }
}
